import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Target,
  Users,
  TrendingUp,
  Handshake,
  MessageSquareQuote,
  Cog,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PeakMark } from "@/components/PeakMark";
import { useSeo } from "@/hooks/useSeo";

const challenges = [
  "Priorities are unclear or constantly shifting",
  "Strategy exists on paper but stalls in execution",
  "Revenue and partnership growth have plateaued",
  "Messaging doesn't match the mission",
  "Teams are busy but not aligned or accountable",
  "Fundraising and sponsorship lack a real pipeline",
];

const services = [
  { icon: Target, title: "Strategic Planning", body: "Turn a broad vision into clear priorities, owners, and measurable outcomes your team can actually execute." },
  { icon: Users, title: "Leadership Alignment", body: "Get your leadership team rowing in the same direction with shared priorities and honest accountability." },
  { icon: TrendingUp, title: "Revenue Strategy", body: "Build a practical path to sustainable revenue growth — not more activity, but the right activity." },
  { icon: Handshake, title: "Corporate Partnerships & Sponsorship", body: "Develop partnerships that create real value: pipeline design, positioning, proposals, and stewardship." },
  { icon: MessageSquareQuote, title: "Messaging & Positioning", body: "Sharpen how you tell your story so the right people understand your value and act on it." },
  { icon: Cog, title: "Systems & Execution", body: "Install the operating cadence, accountability, and project structure that turn plans into results." },
];

const approach = [
  { n: "01", title: "Clarify", body: "We start with an honest assessment of where the organization is, what matters most, and where the real constraints live." },
  { n: "02", title: "Strategize", body: "We build a focused plan — priorities, owners, measures, and a realistic sequence your team can carry." },
  { n: "03", title: "Execute", body: "We install the cadence and accountability that turn the plan into weekly progress, not another document on a shelf." },
];

const faqs = [
  { q: "Who do you work with?", a: "Nonprofit leaders, founders, small businesses, and mission-driven organizations that need clarity, stronger systems, and execution support." },
  { q: "Is consulting done remotely?", a: "Yes. Engagements are designed to work virtually with organizations nationally, with local presence across the Northern Virginia and Washington, DC region." },
  { q: "What does an engagement look like?", a: "It depends on your goals — from focused strategic planning to ongoing execution support. Most begin with a strategy call to scope the right fit." },
  { q: "Do you help with fundraising and sponsorships?", a: "Yes. Corporate partnership development, sponsorship strategy, and nonprofit revenue strategy are core areas." },
  { q: "How is this different from a traditional consultant?", a: "The work doesn't end at a recommendation. The focus is execution — building the systems and accountability that actually change the organization." },
];

export default function ConsultingPage() {
  useSeo({
    title: "Strategic Consulting for Leaders and Organizations | SSG",
    description:
      "Turn priorities into execution with strategic consulting for nonprofit leaders, founders, businesses, and mission-driven organizations. Schedule a strategy call.",
    path: "/strategic-consulting",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Strategic Consulting",
      provider: { "@type": "Organization", name: "Smith Strategic Group" },
      areaServed: ["United States", "Northern Virginia", "Washington, DC"],
      description:
        "Strategic planning, revenue strategy, corporate partnerships, fundraising, messaging, and execution support for leaders and organizations.",
    },
  });

  return (
    <div className="App font-body antialiased text-neutral-950 bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative bg-[#0A0A0A] text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden ssg-grain" data-testid="consulting-hero">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500 hover:text-white transition-colors mb-8">
            <ArrowLeft size={14} strokeWidth={1.6} /> Back to Smith Strategic Group
          </Link>
          <div className="flex items-center gap-4 mb-8">
            <PeakMark size={30} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">Strategic Consulting</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[1.02] max-w-4xl">
            Clarity is valuable.{" "}
            <span className="italic font-light">Execution</span> is what changes the organization.
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-neutral-300 leading-relaxed font-medium">
            Strategic consulting for nonprofit leaders, founders, and mission-driven organizations that need sharper priorities, stronger systems, and a plan they can actually execute.
          </p>
          <div className="mt-10">
            <Link to="/contact" data-testid="consulting-hero-cta" className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300">
              Schedule a Strategy Call
              <ArrowUpRight size={16} strokeWidth={1.6} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="py-24 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">The Challenges We Solve</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-neutral-950 leading-[1.05] max-w-3xl">
            When strategy stalls, it&apos;s rarely a <span className="italic font-light">vision problem</span>.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {challenges.map((c, i) => (
              <div key={i} className="bg-white p-8 text-[15px] text-neutral-700 font-medium leading-relaxed flex items-start gap-4">
                <span className="mt-2 w-3 h-px bg-[#9B1C1C] flex-none" />
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-24 md:py-28 bg-[#F7F6F3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">Service Areas</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-neutral-950 leading-[1.05] max-w-3xl">
            Focused help where it moves the needle.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white p-8 lg:p-10">
                  <div className="relative flex items-center justify-center w-14 h-14 mb-6">
                    <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/40" />
                    <span className="absolute inset-1.5 rounded-full bg-[#9B1C1C]/8" />
                    <Icon size={22} strokeWidth={1.5} className="text-neutral-900 relative" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-neutral-950">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-neutral-600 font-medium">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 md:py-28 bg-[#0A0A0A] text-white ssg-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">The SSG Approach</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
            Clarify. Strategize. <span className="text-[#E25555]">Execute.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {approach.map((a) => (
              <div key={a.n} className="bg-[#0A0A0A] p-10">
                <span className="font-display text-sm font-bold tracking-[0.2em] text-[#E25555]">/{a.n}</span>
                <h3 className="mt-4 font-display text-2xl font-black tracking-tight">{a.title}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-neutral-400 font-medium">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <section className="py-24 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <PeakMark size={20} className="text-neutral-900" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">Why Brady</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight text-neutral-950 leading-tight">
              A decade of building <span className="italic font-light">people and systems</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 text-[17px] leading-relaxed text-neutral-700 font-medium space-y-6">
            <p>Brady Smith has spent more than a decade at the intersection of leadership, growth, and communication — leading teams, running campaigns, raising meaningful capital, and building the systems that turn strategy into results.</p>
            <p>That experience spans corporate partnership development, nonprofit fundraising, sponsorship acquisition, managing significant revenue portfolios, strategic planning, campaign and event leadership, and cross-functional collaboration inside mission-driven organizations.</p>
            <p>The throughline: closing the gap between where an organization is and what it&apos;s capable of becoming — practically, and with accountability.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-28 bg-[#F7F6F3]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">Common Questions</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-neutral-950 leading-[1.05]">The honest answers.</h2>
          <div className="mt-12 border-t border-neutral-300">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-neutral-300 py-8 grid md:grid-cols-12 gap-6">
                <div className="md:col-span-5 flex items-start gap-4">
                  <span className="font-display text-sm font-bold tracking-[0.2em] text-[#9B1C1C] mt-1">/0{i + 1}</span>
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-neutral-950 leading-snug">{f.q}</h3>
                </div>
                <div className="md:col-span-7 text-[15px] text-neutral-700 leading-relaxed font-medium">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-28 bg-[#0A0A0A] text-white ssg-grain text-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <PeakMark size={40} className="text-[#E25555] mx-auto mb-8" />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Let&apos;s turn priorities<br />into <span className="italic font-light">execution</span>.
          </h2>
          <div className="mt-12">
            <Link to="/contact" data-testid="consulting-final-cta" className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300">
              Schedule a Strategy Call
              <ArrowUpRight size={16} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
